import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {ErrorMessage, Form, Formik} from "formik";
import * as Yup from "yup";
import YupPassword from "yup-password";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { Link } from "react-router-dom";

import {resetPassword} from "../slices/auth";
import {clearMessage} from "../slices/message";
import {LOCALES} from "../i18n/locales";
import {messages} from "../i18n/messages";
import PasswordField from "./PasswordField";

const ResetPassword = () => {
  const [successful, setSuccessful] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { message } = useSelector((state) => state.message);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  const initialValues = {
    newPassword: "",
    matchingPassword: "",
  };

  YupPassword(Yup);

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string().password()
        .min(8, messages[LOCALES.ENGLISH].password_length)
        .max(30, messages[LOCALES.ENGLISH].password_length)
        .minUppercase(1, messages[LOCALES.ENGLISH].password_upper_case)
        .minNumbers(1, messages[LOCALES.ENGLISH].password_number)
        .minSymbols(1, messages[LOCALES.ENGLISH].password_symbol)
        .required(messages[LOCALES.ENGLISH].field_required),
    matchingPassword: Yup.string()
        .min(8, messages[LOCALES.ENGLISH].password_length)
        .max(30, messages[LOCALES.ENGLISH].password_length)
        .minUppercase(1, messages[LOCALES.ENGLISH].password_upper_case)
        .minNumbers(1, messages[LOCALES.ENGLISH].password_number)
        .minSymbols(1, messages[LOCALES.ENGLISH].password_symbol)
        .oneOf([Yup.ref("newPassword"), null], messages[LOCALES.ENGLISH].password_match)
        .required(messages[LOCALES.ENGLISH].field_required),
  });

  const handleResetPassword = (formValue) => {
    const { newPassword } = formValue;

    setSuccessful(false);
    setIsLoading(true);

    dispatch(resetPassword({ newPassword, token }))
      .unwrap()
      .then(() => {
        setSuccessful(true);
        setIsLoading(false);
      })
      .catch(() => {
        setSuccessful(false);
        setIsLoading(false);
      });
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={5}>
          <Card className="border-0 shadow-lg" style={{ borderRadius: '24px' }}>
            <div 
              style={{ 
                height: '120px', 
                background: 'var(--primary-gradient)', 
                borderTopLeftRadius: '24px', 
                borderTopRightRadius: '24px' 
              }}
            ></div>
            
            <Card.Body className="px-4 pb-5 pt-0">
              <div 
                className="mx-auto rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm"
                style={{
                  width: '80px',
                  height: '80px',
                  marginTop: '-40px',
                  marginBottom: '20px',
                  border: '4px solid white',
                  fontSize: '2rem',
                  color: '#4f46e5'
                }}
              >
                <i className="bi bi-shield-lock-fill"></i>
              </div>

              <h3 className="fw-bold text-center mb-4" style={{ color: 'var(--text-main)' }}>
                Reset Password
              </h3>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleResetPassword}
              >
                <Form>
                  {!successful && (
                    <div className="d-flex flex-column gap-3">
                      <div>
                        <label htmlFor="newPassword" className="text-muted small fw-bold mb-1">New Password</label>
                        <PasswordField name="newPassword" />
                        <ErrorMessage
                          name="newPassword"
                          component="div"
                          className="text-danger small mt-1 fw-medium"
                        />
                      </div>

                      <div className="mb-2">
                        <label htmlFor="matchingPassword" className="text-muted small fw-bold mb-1">Repeat Password</label>
                        <PasswordField name="matchingPassword" />
                        <ErrorMessage
                          name="matchingPassword"
                          component="div"
                          className="text-danger small mt-1 fw-medium"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        className="btn-premium w-100 rounded-pill py-2 mt-2" 
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <span className="spinner-border spinner-border-sm me-2"></span>
                        ) : (
                          <i className="bi bi-check2-circle me-2"></i>
                        )}
                        Update Password
                      </Button>
                      
                      <div className="text-center mt-3">
                        <Link to="/login" className="text-decoration-none small fw-bold text-muted">
                          <i className="bi bi-arrow-left me-1"></i> Back to Login
                        </Link>
                      </div>
                    </div>
                  )}
                </Form>
              </Formik>

              {message && (
                <div className="mt-4">
                  <div
                    className={`alert ${successful ? "alert-success" : "alert-danger"} rounded-4 border-0 shadow-sm`}
                    role="alert"
                  >
                    <i className={`bi ${successful ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"} me-2`}></i>
                    {message}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
